import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useIsMobile, useSwipe, useScrollLock } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";

/**
 * BottomSheet - A mobile-friendly sheet that slides up from the bottom
 * On desktop, it renders as a centered dialog
 */
const BottomSheet = DialogPrimitive.Root;

const BottomSheetTrigger = DialogPrimitive.Trigger;

const BottomSheetClose = DialogPrimitive.Close;

const BottomSheetPortal = DialogPrimitive.Portal;

const BottomSheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
BottomSheetOverlay.displayName = "BottomSheetOverlay";

const BottomSheetContent = React.forwardRef(
  (
    {
      className,
      children,
      showHandle = true,
      showCloseButton = true,
      side = "bottom", // 'bottom' | 'center'
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const closeRef = React.useRef(null);
    const [isOpen, setIsOpen] = React.useState(false);

    // Track open state for scroll lock
    React.useEffect(() => {
      setIsOpen(true);
      return () => setIsOpen(false);
    }, []);

    useScrollLock(isMobile && isOpen);

    // Swipe to dismiss on mobile
    const { handlers } = useSwipe({
      onSwipeDown: () => {
        closeRef.current?.click();
      },
      threshold: 50,
    });

    // Desktop: render as centered dialog
    if (!isMobile || side === "center") {
      return (
        <BottomSheetPortal>
          <BottomSheetOverlay />
          <DialogPrimitive.Content
            ref={ref}
            className={cn(
              "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
              "w-full max-w-lg max-h-[85vh]",
              "bg-background border border-border rounded-lg shadow-xl",
              "overflow-hidden",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
              "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
              "duration-200",
              className,
            )}
            {...props}
          >
            {children}
            {showCloseButton && (
              <DialogPrimitive.Close
                ref={closeRef}
                className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </BottomSheetPortal>
      );
    }

    // Mobile: render as bottom sheet
    return (
      <BottomSheetPortal>
        <BottomSheetOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50",
            "w-full max-h-[90vh]",
            "bg-background border-t border-border rounded-t-2xl shadow-xl",
            "overflow-hidden flex flex-col",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "duration-300 ease-out",
            className,
          )}
          style={{
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
          {...props}
        >
          {/* Drag handle */}
          {showHandle && (
            <div
              className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none shrink-0"
              {...handlers}
            >
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
            </div>
          )}
          {children}
          {/* Hidden close button for programmatic close */}
          <DialogPrimitive.Close ref={closeRef} className="hidden" />
        </DialogPrimitive.Content>
      </BottomSheetPortal>
    );
  },
);
BottomSheetContent.displayName = "BottomSheetContent";

const BottomSheetHeader = ({ className, ...props }) => (
  <div
    className={cn("px-4 py-3 border-b border-border shrink-0", className)}
    {...props}
  />
);
BottomSheetHeader.displayName = "BottomSheetHeader";

const BottomSheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
));
BottomSheetTitle.displayName = "BottomSheetTitle";

const BottomSheetDescription = React.forwardRef(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-muted-foreground mt-1", className)}
      {...props}
    />
  ),
);
BottomSheetDescription.displayName = "BottomSheetDescription";

const BottomSheetBody = ({ className, ...props }) => (
  <div
    className={cn(
      "px-4 py-4 overflow-y-auto flex-1 overscroll-contain",
      className,
    )}
    style={{ WebkitOverflowScrolling: "touch" }}
    {...props}
  />
);
BottomSheetBody.displayName = "BottomSheetBody";

const BottomSheetFooter = ({ className, ...props }) => {
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "px-4 py-4 border-t border-border shrink-0",
        "flex gap-2",
        isMobile ? "flex-col" : "flex-row justify-end",
        className,
      )}
      {...props}
    />
  );
};
BottomSheetFooter.displayName = "BottomSheetFooter";

/**
 * ActionSheet - iOS-style action sheet for mobile
 */
const ActionSheet = ({ open, onOpenChange, children }) => (
  <BottomSheet open={open} onOpenChange={onOpenChange}>
    {children}
  </BottomSheet>
);

const ActionSheetTrigger = BottomSheetTrigger;

const ActionSheetContent = React.forwardRef(
  ({ className, title, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    return (
      <BottomSheetContent
        ref={ref}
        showCloseButton={false}
        className={cn("p-0", className)}
        {...props}
      >
        {title && (
          <div className="px-4 py-3 text-center border-b border-border">
            <p className="text-sm text-muted-foreground">{title}</p>
          </div>
        )}
        <div className="py-2">{children}</div>
        {isMobile && (
          <div className="px-4 pb-2">
            <BottomSheetClose asChild>
              <button className="w-full h-12 rounded-xl bg-muted font-medium text-foreground active:bg-muted/80 transition-colors">
                Cancel
              </button>
            </BottomSheetClose>
          </div>
        )}
      </BottomSheetContent>
    );
  },
);
ActionSheetContent.displayName = "ActionSheetContent";

const ActionSheetItem = React.forwardRef(
  ({ className, destructive = false, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "w-full px-4 py-3 text-left",
        "flex items-center gap-3",
        "transition-colors active:bg-muted",
        destructive ? "text-destructive" : "text-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);
ActionSheetItem.displayName = "ActionSheetItem";

const ActionSheetSeparator = () => <div className="h-px bg-border mx-4 my-1" />;

export {
  BottomSheet,
  BottomSheetTrigger,
  BottomSheetClose,
  BottomSheetPortal,
  BottomSheetOverlay,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetDescription,
  BottomSheetBody,
  BottomSheetFooter,
  // Action sheet
  ActionSheet,
  ActionSheetTrigger,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetSeparator,
};
